'use client'

import { ErrorMessage } from '@/components/ui/error-message'
import { Label } from '@/components/ui/label'
import { run } from 'shared'
import { cn } from '@/utils/styles/cn'
import { IChildrenProps } from '@/utils/typings/children-props'
import { AnimatePresence, motion } from 'framer-motion'
import { ReactNode } from 'react'
import { useMeasure } from 'react-use'

export interface ILabeledProps extends IChildrenProps {
    label: ReactNode
    description?: ReactNode
    className?: string
    styleType?: 'small' | 'medium'
    inlineStartNode?: ReactNode
    inlineEndNode?: ReactNode
    error?: string | null
    disabled?: boolean
    hideLabel?: boolean
    labelClassName?: string
    layout?: boolean | 'position'
    required?: boolean
    descriptionClassName?: string
    headerClassName?: string
}

export const Labeled = ({
    children,
    label,
    description,
    className,
    styleType = 'small',
    inlineEndNode,
    inlineStartNode,
    error,
    disabled,
    hideLabel,
    labelClassName,
    layout,
    required,
    descriptionClassName,
    headerClassName,
}: ILabeledProps) => {
    const [ref, { width }] = useMeasure<HTMLDivElement>()

    return (
        <motion.div
            transition={{ layout: { duration: 0.1 } }}
            layout={layout}
            ref={ref}
            className={cn('grid items-center', className)}
        >
            <div className={cn('flex w-full items-end justify-between', headerClassName)}>
                <div className="flex flex-col">
                    {!hideLabel && (
                        <Label
                            className={cn(
                                'dark:text-foreground/50 text-foreground/80 whitespace-nowrap select-none flex items-center gap-1',
                                run(() => {
                                    switch (styleType) {
                                        case 'small':
                                            return 'mb-1 ml-0 text-[13px] font-medium'

                                        case 'medium':
                                            return 'mb-1 text-[15px] font-medium'
                                    }
                                }),
                                disabled && 'opacity-30',
                                labelClassName
                            )}
                        >
                            {label || <span className="text-transparent">F</span>}
                            {run(() => {
                                if (required === undefined || required === null) return

                                return (
                                    <span className="text-foreground" aria-label="required">
                                        {required ? ' *' : ' (optional)'}
                                    </span>
                                )
                            })}

                            {inlineStartNode}
                        </Label>
                    )}

                    {description && (
                        <p
                            className={cn(
                                'dark:text-foreground/40 text-foreground/40 -mt-1',
                                run(() => {
                                    switch (styleType) {
                                        case 'small':
                                            return 'mb-1 text-[12px] font-normal'

                                        case 'medium':
                                            return 'mb-1 text-[14px] font-normal'
                                    }
                                }),
                                disabled && 'opacity-30',
                                descriptionClassName
                            )}
                        >
                            {description}
                        </p>
                    )}
                </div>

                {inlineEndNode}
            </div>

            {children}

            <AnimatePresence initial={false}>
                {error && <ErrorMessage style={{ maxWidth: width }} error={error} />}
            </AnimatePresence>
        </motion.div>
    )
}
